import { Card, CardHeader, Table, Th, Tr, Td, MetricCard } from './ui';
import { fmtCurrencyWhole, fmtPct } from '../lib/fmt';
import type { PlanLineRow } from '../lib/api';

// Shared FP&A summary helpers + cards, used by the plan workspace and the
// exec FP&A dashboard. All derive from the computed company rollup
// (department_id IS NULL); department views use the per-department rows.

export interface ScenarioSummary { revenue: number; variable: number; salary: number; fixed: number; opProfit: number; endingCash: number }

/** Horizon summary from computed plan lines (company rollup rows). */
export function summarize(lines: PlanLineRow[]): ScenarioSummary {
  const rollup = lines.filter((l) => l.department_id === null);
  const sumWhere = (pred: (c: string) => boolean) =>
    rollup.filter((l) => pred(l.line_code)).reduce((s, l) => s + l.amount, 0);
  const revenue = sumWhere((c) => c.startsWith('rev'));
  const variable = sumWhere((c) => c.startsWith('cost.var'));
  const salary = sumWhere((c) => c === 'cost.salary');
  const fixed = sumWhere((c) => c.startsWith('cost.fixed') && c !== 'cost.fixed.total');
  const closings = rollup.filter((l) => l.line_code === 'cash.closing').sort((a, b) => a.period_month.localeCompare(b.period_month));
  const endingCash = closings.length ? closings[closings.length - 1].amount : 0;
  return { revenue, variable, salary, fixed, opProfit: revenue - variable - salary - fixed, endingCash };
}

export const SUMMARY_ROWS: Array<{ label: string; pick: (s: ScenarioSummary) => number; goodWhenUp: boolean }> = [
  { label: 'Revenue', pick: (s) => s.revenue, goodWhenUp: true },
  { label: 'Variable cost', pick: (s) => s.variable, goodWhenUp: false },
  { label: 'Salary', pick: (s) => s.salary, goodWhenUp: false },
  { label: 'Fixed cost', pick: (s) => s.fixed, goodWhenUp: false },
  { label: 'Operating profit', pick: (s) => s.opProfit, goodWhenUp: true },
  { label: 'Ending cash', pick: (s) => s.endingCash, goodWhenUp: true },
];

/** Headline KPI cards for a plan, from the computed company rollup. */
export function PlanKpis({ lines }: { lines: PlanLineRow[] }) {
  const s = summarize(lines);
  const totalCost = s.variable + s.salary + s.fixed;
  const margin = s.revenue !== 0 ? s.opProfit / s.revenue : 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
      <MetricCard label="Revenue" value={fmtCurrencyWhole(s.revenue)} sub="horizon total" />
      <MetricCard label="Total cost" value={fmtCurrencyWhole(totalCost)} sub="salary · variable · fixed" />
      <MetricCard label="Operating profit" value={fmtCurrencyWhole(s.opProfit)} accent={s.opProfit >= 0 ? 'green' : undefined} sub={`${fmtPct(margin)} margin`} />
      <MetricCard label="Ending cash" value={fmtCurrencyWhole(s.endingCash)} accent={s.endingCash >= 0 ? 'blue' : 'amber'} sub="last month" />
    </div>
  );
}

/** Per-department contribution: revenue / cost / operating profit totals. */
export function DepartmentBreakdown({ lines }: { lines: PlanLineRow[] }) {
  const dept = lines.filter((l) => l.department_id !== null);
  if (dept.length === 0) return null;
  const depts = Array.from(new Set(dept.map((l) => l.department_id as string))).sort();
  const idx = new Map<string, number>();
  for (const l of dept) idx.set(`${l.line_code}|${l.department_id}`, (idx.get(`${l.line_code}|${l.department_id}`) ?? 0) + l.amount);
  const v = (code: string, d: string) => idx.get(`${code}|${d}`) ?? 0;
  const rows = depts.map((d) => {
    const revenue = v('rev.tm', d) + v('rev.ff', d) + v('rev.sub', d);
    const cost = v('cost.salary', d) + v('cost.var.labor', d) + v('cost.var.materials', d) + v('cost.fixed.total', d);
    const op = idx.has(`op_profit|${d}`) ? v('op_profit', d) : revenue - cost;
    return { d, revenue: Math.round(revenue), cost: Math.round(cost), op: Math.round(op), margin: revenue !== 0 ? op / revenue : 0 };
  });
  return (
    <Card>
      <CardHeader><div style={{ fontSize: 13, fontWeight: 500 }}>By department</div><span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>contribution over the horizon</span></CardHeader>
      <Table>
        <thead><tr><Th>Department</Th><Th right>Revenue</Th><Th right>Cost</Th><Th right>Operating profit</Th><Th right>Margin</Th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <Tr key={r.d}>
              <Td bold>{r.d}</Td>
              <Td right>{fmtCurrencyWhole(r.revenue)}</Td>
              <Td right muted>{fmtCurrencyWhole(r.cost)}</Td>
              <Td right style={{ color: r.op >= 0 ? 'var(--color-green-text)' : 'var(--color-red-text)' }}>{fmtCurrencyWhole(r.op)}</Td>
              <Td right muted>{fmtPct(r.margin)}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}
